import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Mail, Phone } from "lucide-react";
import { useState } from "react";

const SECTIONS = {
  faq: {
    title: "Frequently Asked Questions",
    intro: "Quick answers to the most common things guests ask before booking a Tarzan retreat.",
    items: [
      ["Where is Tarzan currently available to book?", "Right now, Tarzan Hyderabad Retreat (Nature and Home, Amdapur, Telangana 501504) is the only property open for bookings. Coorg, Wayanad, Munnar and other destinations are coming soon."],
      ["How do I confirm a booking?", "Submit a booking enquiry on any property page. Our concierge will call you within 2 hours on the phone number you provided to confirm dates, pricing and any special requests."],
      ["What time is check-in and check-out?", "Standard check-in is from 3:00 PM and check-out is by 11:00 AM. Flexible timings can be requested during your enquiry."],
      ["Is the pricing per night or per person?", "All rates are per property, per night. The rate covers the entire home for up to the stated maximum number of guests."],
      ["Are meals included?", "A private chef is available on call at every property. Meals are typically billed at cost — the concierge will share menus and pricing when you enquire."],
      ["Do you allow pets?", "Select properties are pet-friendly. Please mention your pet in the booking-enquiry notes so we can confirm."],
      ["What if I need to cancel?", "See our Cancellation Policy for full terms. In brief: full refund up to 14 days before check-in, 50% refund 7-14 days before, no refund inside 7 days — but concierge can help you reschedule."],
    ],
  },
  "booking-policy": {
    title: "Booking Policy",
    intro: "How booking, payment and confirmation work at Tarzan.",
    items: [
      ["Enquiry-first booking", "All Tarzan properties operate on an enquiry-first model. You share preferred dates, guest count and contact details; our concierge personally confirms availability and finalises pricing before any charge."],
      ["Payment & confirmation", "On confirmation the concierge shares a secure payment link. A 50% advance holds the booking; the balance is due 7 days before check-in."],
      ["ID & guest register", "As per Telangana Tourism regulations, we collect a valid photo ID (Aadhaar / passport / driving licence) for every adult guest at check-in."],
      ["House rules", "No loud music after 10 PM, no smoking indoors, no fireworks. The concierge will share the full house guide before your stay."],
      ["Damage & incidentals", "A refundable security deposit of ₹15,000 is collected on booking confirmation for stays of 2+ nights."],
    ],
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    intro: "We understand plans change — here's exactly how cancellations are handled.",
    items: [
      ["More than 14 days before check-in", "100% refund of the amount paid, minus payment-gateway charges. Refunded to the original payment method within 7 working days."],
      ["7 to 14 days before check-in", "50% refund of the amount paid. The remaining 50% can be used as credit against a future Tarzan stay within 12 months."],
      ["Less than 7 days before check-in", "No monetary refund; however, our concierge will actively help you reschedule to another available date within 6 months at no additional cost."],
      ["Force majeure", "In case of flood, cyclone, government-mandated lockdowns or other force majeure events, full credit will be issued regardless of the cancellation window."],
      ["Same-day no-shows", "Treated as a full-price stay. Please call our concierge at +91 94913 17777 if you're running late — we'll do everything we can to help."],
    ],
  },
  "help": {
    title: "Help Centre",
    intro: "Talk to us. We're actual humans and respond fast.",
    items: [
      ["Concierge (24×7)", "+91 94913 17777"],
      ["Email", "hello@tarzanretreat.com"],
      ["Booking help", "Use the booking-enquiry form on any property page — the concierge calls back within 2 hours."],
      ["Existing booking changes", "Call the concierge line or reply to your booking confirmation email with your booking ID."],
      ["Feedback & press", "Please write to hello@tarzanretreat.com and we'll route to the right team."],
    ],
  },
  "sustainability": {
    title: "Sustainability at Tarzan",
    intro: "We build slowly, source locally and measure what matters.",
    items: [
      ["Aligned with UN SDGs", "Our operations are designed against four UN Sustainable Development Goals — 11 (Sustainable Cities & Communities), 12 (Responsible Consumption & Production), 13 (Climate Action) and 15 (Life on Land)."],
      ["Local sourcing", "80%+ of our produce, textiles and craft is sourced within a 100 km radius of each property, directly from farmers and artisans."],
      ["Low-impact construction", "New builds use rammed earth, reclaimed timber and stone from within the property's district — no imported marble, no synthetic paints."],
      ["Water & energy", "Every retreat runs on rainwater harvesting plus solar assist. Grey-water from bathrooms is filtered and used to irrigate the estate."],
      ["Community", "10% of every booking revenue funds a local scholarship or forest-restoration program near the property. FY26 spend: ₹18.4L."],
    ],
  },
  "list-your-property": {
    title: "List your property with Tarzan",
    intro: "If you own a thoughtfully built home in the lap of nature, we'd love to talk.",
    items: [
      ["What we look for", "Homes on 1+ acre, in nature-rich locations, built with local materials, with 3+ bedrooms and a hands-on host."],
      ["What we offer", "End-to-end management: listing, marketing, guest concierge, housekeeping, revenue collection, damage protection. You keep 70% of net revenue."],
      ["Timeline", "From first call to first booking: typically 30–45 days. See our Become a Host page for the full onboarding process."],
      ["Get in touch", "Call +91 94913 17777 or write to hosts@tarzanretreat.com with a few photos and the property location."],
    ],
  },
};

export default function LegalPage({ slug }) {
  const s = SECTIONS[slug] || SECTIONS.faq;
  const [open, setOpen] = useState(0);
  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen">
      <div className="container-tarzan max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-forest mb-6"><ArrowLeft className="h-4 w-4" /> Home</Link>
        <div className="eyebrow mb-3">{slug === "faq" ? "FAQ" : slug === "help" ? "Support" : slug === "sustainability" ? "Our Commitment" : "Policy"}</div>
        <h1 className="font-serif text-5xl md:text-6xl text-ink tracking-tight leading-[1.02]">{s.title}</h1>
        <p className="text-ink-soft mt-5 text-base md:text-lg leading-relaxed">{s.intro}</p>

        <div className="mt-12 space-y-3" data-testid={`legal-${slug}`}>
          {s.items.map(([q, a], i) => (
            <div key={q} className="bg-white border border-forest/10 rounded-2xl overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="font-serif text-lg text-ink">{q}</span>
                <ChevronDown className={`h-4 w-4 text-forest transition-transform shrink-0 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-6 pb-6 -mt-1 text-ink-soft leading-relaxed">{a}</div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 bg-forest-50/60 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="eyebrow mb-2">Still have questions?</div>
            <div className="font-serif text-2xl text-ink">Our concierge is on call.</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="tel:+919491317777" className="inline-flex items-center justify-center gap-2 bg-forest text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-forest-700 transition-colors"><Phone className="h-4 w-4" /> +91 94913 17777</a>
            <a href="mailto:hello@tarzanretreat.com" className="inline-flex items-center justify-center gap-2 border border-forest/30 text-forest px-6 py-3 rounded-full text-sm font-medium hover:bg-forest hover:text-ivory transition-colors"><Mail className="h-4 w-4" /> Email us</a>
          </div>
        </div>
      </div>
    </div>
  );
}
